import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { alpha } from '@mui/material/styles';

import { Header } from './shared/components/Header';
import { RootState, AppDispatch } from './redux/store';
import { fetchUsers } from './users/redux/usersSlice';
import getTheme from './shared/config/theme';
import { resolveThemeMode } from './shared/redux/preferencesSlice';
import { ChatScene } from './scene/ChatScene';
import { useVectorMapFeed } from './scene/useVectorMapFeed';

import CollectionsPage from './pages/CollectionsPage';
import UsersPage from './pages/UsersPage';
import UserMessagesPage from './pages/UserMessagesPage';
import UploadsPage from './pages/UploadsPage';
import MemoryPage from './pages/MemoryPage';
import ProfilePage from './pages/ProfilePage';
import PromptsPage from './pages/PromptsPage';
import QualityPage from './pages/QualityPage';
import RemindersPage from './pages/RemindersPage';
import VectorSpacePage from './pages/VectorSpacePage';

import './App.css';

const PromptEditPage = React.lazy(() => import('./pages/PromptEditPage'));

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const themeMode = useSelector((state: RootState) => state.preferences?.theme) || 'dark';
  const colorPalette = useSelector((state: RootState) => state.preferences?.colorPalette);
  const resolvedMode = resolveThemeMode(themeMode);
  const muiTheme = useMemo(
    () => getTheme(resolvedMode, colorPalette),
    [resolvedMode, colorPalette]
  );

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useVectorMapFeed();

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <ChatScene />
      <Router>
        <div
          className={`app ${resolvedMode}`}
          data-theme={resolvedMode}
          style={
            {
              '--surface': alpha(muiTheme.palette.background.paper, 0.5),
              '--border': muiTheme.palette.divider,
              '--text-muted': muiTheme.palette.text.secondary,
            } as React.CSSProperties
          }
        >
          <Header>
            <div className="app-container">
              <main className="app-main">
                <Routes>
                  <Route path="/collections" element={<CollectionsPage />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/messages" element={<UserMessagesPage />} />
                  <Route path="/messages/:userId" element={<UserMessagesPage />} />
                  <Route path="/uploads/:uploadId?" element={<UploadsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/memory" element={<MemoryPage />} />
                  <Route path="/prompts" element={<PromptsPage />} />
                  <Route path="/quality" element={<QualityPage />} />
                  <Route path="/reminders" element={<RemindersPage />} />
                  <Route
                    path="/prompts/:id/edit"
                    element={
                      <React.Suspense fallback={null}>
                        <PromptEditPage />
                      </React.Suspense>
                    }
                  />
                  <Route path="/vector-space" element={<VectorSpacePage />} />
                  <Route path="/" element={<Navigate to="/vector-space" replace />} />
                </Routes>
              </main>
            </div>
          </Header>
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;
